/**
 * DraftPageClient — landing page for /draft.
 * Replaces static DraftClient with dynamic version that uses
 * reference template data, Firestore draft list, and chat input.
 *
 * Track C3.
 */
"use client";

import { useState, useMemo, useCallback, useRef } from "react";
import { useRouter } from "next/navigation";
import { PageHero } from "@/shared/components/ui/PageHero";
import { useDraftList } from "@/modes/draft/hooks/useDraftList";
import { createDraft, updateDraft, uploadKnowledgeDocument, uploadKnowledgeDocumentV2, countUserDrafts, toKnowledgeFileRef } from "@/modes/draft/api";
import { demoTemplates, getCategoryCounts } from "@/modes/draft/data/templates";
import { AuthPromptModal } from "@/modes/draft/components/shared/AuthPromptModal";
import { useAuthContext } from "@/platform/auth/AuthContext";
import { isScmdojoUser } from "@/platform/auth/isScmdojoUser";
import type { Draft, KnowledgeFileRef } from "@/modes/draft/types";

const CATEGORIES = Object.keys(getCategoryCounts()).sort();

const CAT_CLS: Record<string, string> = {
  "Strategy & Governance":  "d-strategy",
  "Operations & Planning":  "d-inventory",
  "Supplier Management":    "d-srm",
  "Logistics & Transport":  "d-logistics",
  "Capability & Training":  "d-sop",
  "Process & SOPs":         "d-order",
  Procurement:              "d-procurement",
};

export function DraftPageClient() {
  const router = useRouter();
  const { user, profile } = useAuthContext();
  const { drafts, loading: draftsLoading, remove } = useDraftList();
  const [tab, setTab] = useState<"new" | "saved">("new");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [docType, setDocType] = useState<"DOC" | "PPT">("DOC");
  const [deckVersion, setDeckVersion] = useState<"v1" | "v2">("v2");
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [draftLimitReached, setDraftLimitReached] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return demoTemplates.filter((t) => {
      const matchQ =
        !q ||
        t.title.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q);
      const matchC = category === "all" || t.category === category;
      return matchQ && matchC;
    });
  }, [search, category]);

  const handleSend = useCallback(
    async (message: string, files?: File[]) => {
      if (!user) {
        setShowAuthModal(true);
        return;
      }

      // Free plan: block if user already has 2+ saved drafts
      const sub = profile?.subscription;
      const tier = sub?.tier ?? (sub?.plan === "pro_monthly" || sub?.plan === "pro_annual" ? "pro" : sub?.plan === "enterprise" ? "enterprise" : "free");
      if (tier === "free") {
        try {
          const count = await countUserDrafts(user.uid);
          if (count >= 2) {
            setDraftLimitReached(true);
            return;
          }
        } catch (err) {
          console.error("Failed to check draft count:", err);
        }
      }

      setError(null);
      setSending(true);
      try {
        const allFiles = [...(files ?? []), ...attachments];
        const isPpt = docType === "PPT";
        // Derive smart title from user query instead of hardcoding "New Draft"
        const smartTitle = message.trim().replace(/[.!?]+$/, "").split(/\s+/).slice(0, 8).join(" ");
        const draftTitle = smartTitle.charAt(0).toUpperCase() + smartTitle.slice(1);
        const draft: Draft = await createDraft(
          user.uid,
          draftTitle || "New Draft",
          message,
          undefined,
          isPpt ? "deck" : "doc"
        );

        // Upload attachments — a failure here must NOT abort the flow. The
        // draft is already saved, so still navigate to it and surface any
        // upload problem there (the canvas lets the user retry the upload).
        const uploadErrors: string[] = [];
        const uploadedRefs: KnowledgeFileRef[] = [];
        if (allFiles.length) {
          const draftId = `draft-${draft.id}`;
          const isV2Deck = isPpt && deckVersion === "v2";

          if (isV2Deck) {
            // v2 deck: upload via FAISS + Anthropic Files API path (no file refs needed)
            await Promise.all(
              allFiles.map((f) => uploadKnowledgeDocumentV2(f, draftId))
            );
          } else {
            // v1 / doc: use allSettled + persist file refs to Firestore
            const results = await Promise.allSettled(
              allFiles.map((f) => uploadKnowledgeDocument(f, draftId))
            );
            results.forEach((r, i) => {
              if (r.status === "rejected") {
                console.error("Failed to upload knowledge document:", r.reason);
                uploadErrors.push(
                  r.reason instanceof Error && r.reason.message
                    ? r.reason.message
                    : "Upload failed"
                );
              } else {
                const file = allFiles[i];
                uploadedRefs.push(toKnowledgeFileRef(file, r.value.doc_id));
              }
            });
            if (uploadedRefs.length) {
              updateDraft(draft.id, { knowledgeFiles: uploadedRefs }).catch((err) =>
                console.error("Failed to persist knowledge files:", err)
              );
            }
          }
        }
        let path: string;
        if (!isPpt) {
          path = `/draft/draft-${draft.id}`;
        } else if (deckVersion === "v2") {
          path = `/deck/draft-${draft.id}/plan`;
        } else {
          path = `/ppt/draft-${draft.id}`;
        }
        const dest =
          isPpt && deckVersion === "v2"
            ? `${path}?q=${encodeURIComponent(message)}${allFiles.length ? `&files=${encodeURIComponent(allFiles.map(f => f.name).join(","))}` : ""}`
            : `${path}?auto=${encodeURIComponent(message)}`;
        const params = new URLSearchParams();
        if (uploadErrors.length) params.set("uploadError", uploadErrors.join("; "));
        const finalDest = uploadErrors.length ? `${dest}&${params.toString()}` : dest;
        router.push(finalDest);
      } catch (err) {
        console.error("Failed to create draft:", err);
        setError(
          err instanceof Error && err.message
            ? err.message
            : "Failed to create draft. Please try again."
        );
      } finally {
        setSending(false);
        setAttachments([]);
      }
    },
    [user, profile, docType, deckVersion, attachments, router]
  );

  const handleTemplateClick = useCallback(
    async (template: (typeof demoTemplates)[number]) => {
      if (!user) {
        setShowAuthModal(true);
        return;
      }

      // Free plan: block if user already has 2+ saved drafts
      const sub = profile?.subscription;
      const tier = sub?.tier ?? (sub?.plan === "pro_monthly" || sub?.plan === "pro_annual" ? "pro" : sub?.plan === "enterprise" ? "enterprise" : "free");
      if (tier === "free") {
        try {
          const count = await countUserDrafts(user.uid);
          if (count >= 2) {
            setDraftLimitReached(true);
            document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" });
            return;
          }
        } catch (err) {
          console.error("Failed to check draft count:", err);
        }
      }

      setError(null);
      try {
        const isPpt = template.format === "PPT";
        const mode = isPpt ? "deck" : "doc";
        const query = isPpt
          ? template.title
          : `Please generate a document titled "${template.title}".`;
        const draft: Draft = await createDraft(
          user.uid,
          template.title,
          query,
          undefined,
          mode
        );
        const path = isPpt
          ? deckVersion === "v2" ? `/deck/draft-${draft.id}/plan` : `/ppt/draft-${draft.id}`
          : `/draft/draft-${draft.id}`;
        const dest =
          isPpt && deckVersion === "v2"
            ? `${path}?q=${encodeURIComponent(query)}`
            : `${path}?auto=${encodeURIComponent(query)}`;
        router.push(dest);
      } catch (err) {
        console.error("Failed to create draft:", err);
        setError(
          err instanceof Error && err.message
            ? err.message
            : "Failed to create draft. Please try again."
        );
      }
    },
    [user, profile, deckVersion, router]
  );

  return (
    <>
      {showAuthModal && (
        <AuthPromptModal onDismiss={() => setShowAuthModal(false)} />
      )}
      <PageHero
        icon={
          <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="url(#draft-hero-grad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <defs>
              <linearGradient id="draft-hero-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#5B6CFF" />
                <stop offset="100%" stopColor="#EC4899" />
              </linearGradient>
            </defs>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="9" y1="13" x2="15" y2="13" />
            <line x1="9" y1="17" x2="13" y2="17" />
          </svg>
        }
        title={
          <>
            <span className="grad-text">sensei.</span> Drafts
          </>
        }
        subtitle="Generate professional-grade supply chain management documents"
      >
        {/* Deck engine toggle — Legacy (v1) / Latest (v2), visible to all users */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          marginBottom: 4,
        }}>
          <span style={{ fontSize: 11, color: "var(--ink-mute)", fontWeight: 500 }}>Deck engine:</span>
          <div style={{ display: "flex", borderRadius: 8, border: "1px solid var(--line-strong)", overflow: "hidden" }}>
            <button
              type="button"
              onClick={() => setDeckVersion("v1")}
              style={{
                padding: "4px 14px",
                fontSize: 11,
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
                background: deckVersion === "v1" ? "var(--indigo)" : "var(--paper-2)",
                color: deckVersion === "v1" ? "#fff" : "var(--ink-soft)",
                transition: "all .15s",
              }}
            >
              Legacy
            </button>
            <button
              type="button"
              onClick={() => setDeckVersion("v2")}
              style={{
                padding: "4px 14px",
                fontSize: 11,
                fontWeight: 600,
                border: "none",
                borderLeft: "1px solid var(--line-strong)",
                cursor: "pointer",
                background: deckVersion === "v2" ? "var(--indigo)" : "var(--paper-2)",
                color: deckVersion === "v2" ? "#fff" : "var(--ink-soft)",
                transition: "all .15s",
              }}
            >
              Latest
            </button>
          </div>
        </div>

        <div className="diag-tabs">
          <button
            className={`diag-tab${tab === "new" ? " active" : ""}`}
            onClick={() => setTab("new")}
            type="button"
          >
            New Draft
          </button>
          <button
            className={`diag-tab${tab === "saved" ? " active" : ""}`}
            onClick={() => setTab("saved")}
            type="button"
          >
            Saved Drafts <span style={{ opacity: 0.6 }}>({drafts.length})</span>
          </button>
        </div>

        {/* Login banner — shown only to unauthenticated users * /}
        {!isAuthed && !authLoading && (
          <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", borderRadius: 16, border: "1px solid rgba(91,108,255,0.3)", background: "rgba(91,108,255,0.08)", maxWidth: 600, margin: "0 auto 16px", width: "100%" }}>
            <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 12, background: "rgba(91,108,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }} aria-hidden="true">
              🔒
            </div>
            <div style={{ flex: 1, textAlign: "left" }}>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>Sign in to create drafts</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, opacity: 0.7 }}>
                Create a free account or sign in to generate and save supply chain documents.
              </p>
            </div>
            <a
              href="/login"
              style={{ flexShrink: 0, padding: "8px 18px", borderRadius: 10, background: "linear-gradient(135deg,#5B6CFF,#EC4899)", color: "#fff", fontSize: 13, fontWeight: 600, textDecoration: "none" }}
            >
              Sign in
            </a>
          </div>
        )} */}

        {/* Draft limit reached banner */}
        {draftLimitReached && (
          <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", borderRadius: 16, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.08)", maxWidth: 600, margin: "0 auto", width: "100%" }}>
            <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 12, background: "rgba(239,68,68,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }} aria-hidden="true">
              ⚠️
            </div>
            <div style={{ flex: 1, textAlign: "left" }}>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>Free plan limit reached</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, opacity: 0.7 }}>
                You&apos;ve created 2 saved drafts. Upgrade to Pro to create unlimited documents.
              </p>
            </div>
            <a
              href="/pricing"
              style={{ flexShrink: 0, padding: "8px 18px", borderRadius: 10, background: "linear-gradient(135deg,#5B6CFF,#EC4899)", color: "#fff", fontSize: 13, fontWeight: 600, textDecoration: "none" }}
            >
              Upgrade
            </a>
            <button
              type="button"
              onClick={() => setDraftLimitReached(false)}
              style={{ flexShrink: 0, background: "none", border: "none", color: "rgba(239,68,68,0.6)", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 0 }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 16, padding: "14px 20px", borderRadius: 16, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.08)", maxWidth: 600, margin: "0 auto", width: "100%" }}>
            <div style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 12, background: "rgba(239,68,68,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }} aria-hidden="true">
              ⚠️
            </div>
            <div style={{ flex: 1, textAlign: "left" }}>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>Couldn&apos;t create draft</p>
              <p style={{ margin: "2px 0 0", fontSize: 12, opacity: 0.7 }}>
                {error}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setError(null)}
              style={{ flexShrink: 0, background: "none", border: "none", color: "rgba(239,68,68,0.6)", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 0 }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        <form
          className="ask-bar"
          onSubmit={(e) => {
            e.preventDefault();
            const textarea = e.currentTarget.querySelector("textarea") as HTMLTextAreaElement;
            if (textarea?.value.trim()) {
              handleSend(textarea.value.trim());
              textarea.value = "";
              textarea.style.height = "48px";
            }
          }}
        >
          <textarea
            placeholder="I want to draft a procurement strategy document…"
            aria-label="Draft input"
            disabled={sending}
            rows={1}
            style={{ flex: 1, resize: "none", overflow: "hidden", minHeight: 48, maxHeight: 200, height: 48 }}
            onInput={(e) => {
              const el = e.currentTarget;
              el.style.height = "48px";
              requestAnimationFrame(() => {
                el.style.height = Math.min(el.scrollHeight, 200) + "px";
              });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                e.currentTarget.form?.requestSubmit();
              }
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {/* Doc type toggle */}
            <div style={{ display: "flex", borderRadius: 8, border: "1px solid var(--line-strong)", overflow: "hidden" }}>
              <button
                type="button"
                onClick={() => setDocType("DOC")}
                style={{
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  border: "none",
                  cursor: "pointer",
                  background: docType === "DOC" ? "var(--indigo)" : "var(--paper-2)",
                  color: docType === "DOC" ? "#fff" : "var(--ink-soft)",
                  transition: "all .15s",
                }}
              >
                DOC
              </button>
              <button
                type="button"
                onClick={() => setDocType("PPT")}
                style={{
                  padding: "6px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  border: "none",
                  borderLeft: "1px solid var(--line-strong)",
                  cursor: "pointer",
                  background: docType === "PPT" ? "var(--indigo)" : "var(--paper-2)",
                  color: docType === "PPT" ? "#fff" : "var(--ink-soft)",
                  transition: "all .15s",
                }}
              >
                PPT
              </button>
            </div>

            {/* Attachment button */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              multiple
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? []);
                const MAX_BYTES = 100 * 1024 * 1024;
                const oversized = picked.filter(f => f.size > MAX_BYTES);
                if (oversized.length > 0) {
                  alert(`File(s) too large: ${oversized.map(f => `"${f.name}" (${(f.size / (1024*1024)).toFixed(1)} MB)`).join(", ")}. Maximum is 100 MB.`);
                  return;
                }
                if (picked.length) setAttachments((prev) => [...prev, ...picked]);
                e.target.value = "";
              }}
              style={{ display: "none" }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              title="Attach files (PDF, Word, Excel)"
              style={{
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid var(--line-strong)",
                background: attachments.length > 0 ? "rgba(91,108,255,0.12)" : "var(--paper-2)",
                color: attachments.length > 0 ? "var(--indigo)" : "var(--ink-mute)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 12,
                fontWeight: 500,
                transition: "all .15s",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
              {attachments.length > 0 ? `${attachments.length}` : "Attach"}
            </button>

            {/* Send button */}
            <button className="ask-send" type="submit" aria-label="Send" disabled={sending}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </form>

        {/* Uploading / generating loader */}
        {sending && (
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "10px 0",
            fontSize: 13,
            color: "var(--ink-soft)",
          }}>
            <span style={{
              width: 14,
              height: 14,
              border: "2px solid var(--line-strong)",
              borderTopColor: "var(--indigo)",
              borderRadius: "50%",
              animation: "spin 0.6s linear infinite",
              flexShrink: 0,
            }} />
            {attachments.length > 0
              ? "Uploading files & generating draft…"
              : "Generating draft…"}
          </div>
        )}

        {/* Attachment chips */}
        {attachments.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxWidth: 600, margin: "0 auto" }}>
            {attachments.map((f, i) => (
              <span
                key={`${f.name}-${i}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "3px 8px",
                  borderRadius: 6,
                  background: "rgba(91,108,255,0.1)",
                  border: "1px solid rgba(91,108,255,0.2)",
                  color: "var(--indigo)",
                  fontSize: 11,
                  fontWeight: 500,
                }}
              >
                {f.name.length > 20 ? f.name.slice(0, 18) + "…" : f.name}
                <button
                  type="button"
                  onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))}
                  style={{ background: "none", border: "none", color: "var(--indigo)", cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="ask-disclaimer">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          sensei. provides guidance — always double-check critical supply chain decisions
        </div>

        <div className="prompt-chips">
            <button className="prompt-chip" type="button" onClick={() => handleSend("Create a supplier evaluation framework")}>
              Create a supplier evaluation framework
            </button>
            <button className="prompt-chip" type="button" onClick={() => handleSend("Draft a procurement policy document")}>
              Draft a procurement policy document
            </button>
            <button className="prompt-chip" type="button" onClick={() => handleSend("Generate a risk assessment template")}>
              Generate a risk assessment template
            </button>
            <button className="prompt-chip" type="button" onClick={() => handleSend("Build a contract negotiation guide")}>
              Build a contract negotiation guide
            </button>
          </div>
      </PageHero>

      <div className="page">
        {tab === "new" && (
          <>
            <div className="toolbar">
              <div className="search-box">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  type="text"
                  placeholder="Search templates…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search templates"
                />
              </div>

              <div className="domain-select">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  aria-label="Filter by category"
                >
                  <option value="all">All Categories</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
                <span className="chev" aria-hidden="true">
                  ▾
                </span>
              </div>
            </div>

            <p className="results-count">
              Showing <strong>{filtered.length}</strong> template
              {filtered.length !== 1 ? "s" : ""}
            </p>

            <div className="wf-grid">
              {filtered.map((t) => {
                const cls = CAT_CLS[t.category] ?? "d-supplychain";
                return (
                  <article key={t.id} className={`diag-card ${cls}`}>
                    <div className="card-head">
                      <div className="doc-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      </div>
                      <span className="doc-badge">{t.format === "DOCX" ? "DOC" : t.format}</span>
                    </div>
                    <h3>{t.title}</h3>
                    <p>{t.description}</p>
                    <span className="doc-cat">{t.category}</span>
                    <div className="doc-sections">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polygon points="12 2 2 7 12 12 22 7 12 2" />
                        <polyline points="2 17 12 22 22 17" />
                        <polyline points="2 12 12 17 22 12" />
                      </svg>
                      {t.sections} sections
                    </div>
                    <button
                      className="doc-btn"
                      type="button"
                      onClick={() => handleTemplateClick(t)}
                    >
                      Use Template
                    </button>
                  </article>
                );
              })}
            </div>
          </>
        )}

        {tab === "saved" && (
          <>
            {draftsLoading ? (
              <div className="empty-state">
                <p>Loading drafts…</p>
              </div>
            ) : drafts.length === 0 ? (
              <div className="empty-state">
                <svg width="54" height="54" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="9" y1="13" x2="15" y2="13" />
                  <line x1="9" y1="17" x2="13" y2="17" />
                </svg>
                <p>No saved drafts yet. Create a document to get started!</p>
              </div>
            ) : (
              <div className="wf-grid">
                {drafts.map((d) => (
                  <article key={d.id} className="diag-card d-supplychain">
                    <div className="card-head">
                      <div className="doc-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      </div>
                      <span className="doc-badge">{d.mode === "deck" ? "PPT" : "DOC"}</span>
                    </div>
                    <h3>{d.title}</h3>
                    <p style={{ opacity: 0.7, fontSize: 13 }}>{d.query}</p>
                    <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
                      <button
                        className="doc-btn"
                        type="button"
                        onClick={() => {
                          const isDeck = d.mode === "deck";
                          const path = !isDeck
                            ? `/draft/draft-${d.id}`
                            : deckVersion === "v2"
                            ? `/deck/draft-${d.id}`
                            : `/ppt/draft-${d.id}`;
                          router.push(path);
                        }}
                        style={{ flex: 1 }}
                      >
                        Open
                      </button>
                      <button
                        className="doc-btn"
                        type="button"
                        onClick={() => remove(d.id)}
                        style={{ flex: 0, padding: "12px 14px", background: "rgba(239,68,68,0.15)", color: "#ef4444" }}
                      >
                        ✕
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
